import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../../components/Logo';
import { acceptAdminInvitation } from '../../services/system';

export function AdminInviteAcceptance() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? '';
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmation, setConfirmation] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [isComplete, setIsComplete] = React.useState(false);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    if (!token) {
      setError('This invitation link is missing its token.');
      return;
    }
    if (!username.trim()) {
      setError('Enter a username for your administrator account.');
      return;
    }
    if (password.length < 8) {
      setError('Password must contain at least 8 characters.');
      return;
    }
    if (password !== confirmation) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await acceptAdminInvitation(token, username.trim(), password);
      setIsComplete(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to accept admin invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="admin-invite-shell">
      <section className="admin-invite-brand">
        <Logo />
        <div>
          <span className="admin-invite-eyebrow">Administrator invitation</span>
          <h1>Join DailyMove operations.</h1>
          <p>Set up your administrator account to review users, KYC requests, transactions, and routes.</p>
        </div>
      </section>

      <section className="admin-invite-form-panel">
        {isComplete ? (
          <div className="admin-invite-card admin-invite-success">
            <span className="admin-invite-success-icon" aria-hidden="true">OK</span>
            <h2>Account ready</h2>
            <p>Your administrator invitation has been accepted. You can now sign in with your new password.</p>
            <button className="btn btn-primary btn-lg btn-block" type="button" onClick={() => navigate('/')}>Go to sign in</button>
          </div>
        ) : (
          <form className="admin-invite-card" onSubmit={submit}>
            <h2>Accept your invitation</h2>
            <p className="muted">Create a secure password for your administrator account.</p>

            {!token ? <div className="kyc-message passengers-error" role="alert">This invitation link is invalid because it has no token.</div> : null}
            {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}

            <label className="form-group">
              <span>Username</span>
              <input autoComplete="username" disabled={isSubmitting || !token} onChange={(event) => setUsername(event.target.value)} placeholder="e.g. new_admin" required value={username} />
            </label>
            <label className="form-group">
              <span>New password</span>
              <input autoComplete="new-password" disabled={isSubmitting || !token} minLength={8} onChange={(event) => setPassword(event.target.value)} placeholder="At least 8 characters" required type="password" value={password} />
            </label>
            <label className="form-group">
              <span>Confirm password</span>
              <input autoComplete="new-password" disabled={isSubmitting || !token} minLength={8} onChange={(event) => setConfirmation(event.target.value)} required type="password" value={confirmation} />
            </label>

            <button className="btn btn-primary btn-lg btn-block" disabled={isSubmitting || !token} type="submit">
              {isSubmitting ? 'Setting up account...' : 'Accept invitation'}
            </button>
            <button className="btn btn-ghost btn-block" type="button" onClick={() => navigate('/')}>Back to sign in</button>
          </form>
        )}
      </section>
    </main>
  );
}
